import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, Modal, Select, Switch } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useCreateAutoJoinRule,
  useUpdateAutoJoinRule,
} from '@/hooks/useAutoJoinRules';
import {
  type ConditionRow,
  conditionToRow,
  newConditionRow,
  rowToCondition,
  rowValid,
} from '@/lib/auto-join-conditions';
import type {
  AutoJoinOperator,
  AutoJoinTargetRole,
  SpaceAutoJoinRule,
} from '@/types/spaces';

interface AutoJoinRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
  /** When set, the modal edits this rule; otherwise it creates a new one. */
  rule?: SpaceAutoJoinRule | null;
}

const ATTRIBUTE_OPTIONS = [
  { value: 'department', label: 'Department' },
  { value: 'manager', label: 'Manager' },
  { value: 'location', label: 'Location' },
];

const OPERATOR_OPTIONS = [
  { value: 'eq', label: 'equals' },
  { value: 'in', label: 'is one of' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'exists', label: 'is set' },
];

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member — read + write' },
  { value: 'admin', label: 'Admin — member powers + manage members' },
  { value: 'viewer', label: 'Viewer — read-only' },
];

export function AutoJoinRuleModal({ isOpen, onClose, spaceId, rule }: AutoJoinRuleModalProps) {
  const { success, error: showError } = useToast();
  const createRule = useCreateAutoJoinRule(spaceId);
  const updateRule = useUpdateAutoJoinRule(spaceId);
  const isEdit = !!rule;
  const pending = createRule.isPending || updateRule.isPending;

  const [name, setName] = useState('');
  const [targetRole, setTargetRole] = useState<AutoJoinTargetRole>('member');
  const [enabled, setEnabled] = useState(true);
  const [rows, setRows] = useState<ConditionRow[]>([newConditionRow()]);

  // Seed the form whenever the modal opens (or the edited rule changes).
  useEffect(() => {
    if (!isOpen) return;
    if (rule) {
      setName(rule.name);
      setTargetRole(rule.targetRole);
      setEnabled(rule.enabled);
      setRows(rule.conditions.length ? rule.conditions.map(conditionToRow) : [newConditionRow()]);
    } else {
      setName('');
      setTargetRole('member');
      setEnabled(true);
      setRows([newConditionRow()]);
    }
  }, [isOpen, rule]);

  const setRow = (id: string, patch: Partial<ConditionRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const canSave =
    name.trim().length > 0 && rows.length > 0 && rows.every(rowValid);

  const handleSubmit = async () => {
    if (!canSave) return;
    const conditions = rows.map(rowToCondition);
    try {
      if (isEdit && rule) {
        await updateRule.mutateAsync({
          ruleId: rule.ruleId,
          input: { name: name.trim(), conditions, targetRole, enabled },
        });
        success('Rule updated');
      } else {
        await createRule.mutateAsync({ name: name.trim(), conditions, targetRole, enabled });
        success('Rule created');
      }
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save rule');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !pending && onClose()}
      title={isEdit ? 'Edit auto-join rule' : 'New auto-join rule'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => !pending && onClose()} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={pending} disabled={!canSave}>
            {isEdit ? 'Save changes' : 'Create rule'}
          </Button>
        </>
      }
    >
      <div className="space-y-5 py-2">
        <Input
          label="Rule name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sales team"
        />

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Conditions</label>
          <p className="text-xs text-muted-foreground">
            A user joins when <span className="font-medium">all</span> of these match. Add
            another rule for an alternative (any rule matching joins the user).
          </p>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="flex items-start gap-2">
                <div className="w-36 shrink-0">
                  <Select
                    options={ATTRIBUTE_OPTIONS}
                    value={r.attribute}
                    onChange={(e: { target: { value: string } }) =>
                      setRow(r.id, { attribute: e.target.value })
                    }
                  />
                </div>
                <div className="w-32 shrink-0">
                  <Select
                    options={OPERATOR_OPTIONS}
                    value={r.operator}
                    onChange={(e: { target: { value: string } }) =>
                      setRow(r.id, { operator: e.target.value as AutoJoinOperator })
                    }
                  />
                </div>
                <div className="flex-1 min-w-0">
                  {r.operator === 'exists' ? (
                    <div className="h-10 flex items-center px-3 text-sm text-muted-foreground">
                      (no value)
                    </div>
                  ) : (
                    <Input
                      value={r.value}
                      onChange={(e) => setRow(r.id, { value: e.target.value })}
                      placeholder={
                        r.operator === 'in'
                          ? 'value1, value2, …'
                          : r.attribute === 'manager'
                            ? "manager's directory value (e.g. email)"
                            : 'value'
                      }
                    />
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Remove condition"
                  className="mt-2 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
                  disabled={rows.length === 1}
                  onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRows((rs) => [...rs, newConditionRow()])}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add condition
          </Button>
        </div>

        <Select
          label="Grant role"
          options={ROLE_OPTIONS}
          value={targetRole}
          onChange={(e: { target: { value: string } }) =>
            setTargetRole(e.target.value as AutoJoinTargetRole)
          }
        />

        <label className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={setEnabled} />
          <span className="text-sm text-foreground">
            Enabled
            <span className="block text-xs text-muted-foreground">
              Disabled rules are ignored and their auto-joined members are removed on next reconcile.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
